import { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router-dom';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterActiveTags from '@src/components/FilterActiveTags/FilterActiveTags';
import type { SetValueProps } from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import FilterContainer from '@src/components/FilterContainer';
import SecondaryPanel, { useSecondaryPanel } from '@src/components/SecondaryPanel/SecondaryPanel';
import { AbsenceFormDrawer } from '@src/packages/absences/components';
import IntlMessages from '@src/util/IntlMessages';

import { WorkJourneyAttentionSection } from './components/WorkJourneyAttentionSection';
import { WorkJourneyCalendarSection } from './components/WorkJourneyCalendarSection';
import { WorkJourneyJourneysSection } from './components/WorkJourneyJourneysSection';
import { WorkJourneyManagementEmptyState } from './components/WorkJourneyManagementEmptyState';
import { WorkJourneyManagementFilters } from './components/WorkJourneyManagementFilters';
import { PlanMonthlyJourneyDrawer } from './components/PlanMonthlyJourneyDrawer';
import { WorkJourneyManagementTabs } from './components/WorkJourneyManagementTabs';
import { WorkJourneyManagementProvider } from './context';
import { useWorkJourneyManagement } from './hooks/useWorkJourneyManagement';
import styles from './styles.module.less';
import {
  filterValueChangeToManagementPatch,
  managementFiltersToFilterValues,
} from './utils/workJourneyManagementFilterPanel';
import { WorkJourneyManagementRoutes } from './WorkJourneyManagementRoutes';

const WorkJourneyManagementView = () => {
  const intl = useIntl();
  const { url } = useRouteMatch();
  const { toggle, isOpen } = useSecondaryPanel();
  const {
    orgId,
    activeTab,
    handleTabChange,
    tabItems,
    journeyOptions,
    filters,
    filtersActive,
    updateFilters,
    resolveUserOption,
    resetFilters,
    userOptions,
    shiftOptions,
    attentionItems,
    attentionTotal,
    attentionPage,
    attentionPageSize,
    onAttentionPageChange,
    journeysSummary,
    journeyItems,
    journeysTotal,
    journeysPage,
    journeysPageSize,
    onJourneysPageChange,
    calendarSummary,
    calendarCells,
    selectedMonth,
    selectedYear,
    goToPreviousMonth,
    goToNextMonth,
    goToPreviousCalendarMonth,
    goToNextCalendarMonth,
    jumpToMonthYear,
    loading,
    error,
    headerActionButtons,
    scheduleAbsence,
    planDrawerOpen,
    closePlanDrawer,
    refreshActiveTab,
  } = useWorkJourneyManagement();

  const filterPanel = useMemo(
    () => (
      <WorkJourneyManagementFilters
        filters={filters}
        organizationId={orgId}
        month={selectedMonth}
        year={selectedYear}
        shiftOptions={shiftOptions}
        journeyOptions={journeyOptions}
        onChange={updateFilters}
        onUserOptionResolved={resolveUserOption}
        onMonthYearChange={jumpToMonthYear}
        onReset={resetFilters}
      />
    ),
    [
      filters,
      journeyOptions,
      jumpToMonthYear,
      orgId,
      resetFilters,
      resolveUserOption,
      selectedMonth,
      selectedYear,
      shiftOptions,
      updateFilters,
    ],
  );

  const filterTagValues = useMemo(
    () =>
      managementFiltersToFilterValues(filters, intl, userOptions, shiftOptions, journeyOptions),
    [filters, intl, journeyOptions, shiftOptions, userOptions],
  );

  const handleCustomFilterChange = (change: SetValueProps) => {
    updateFilters(filterValueChangeToManagementPatch(change));
  };

  const filterToggleLabel = intl.formatMessage({
    id: isOpen ? 'filterDropdown.closeFilters' : 'dashboard.tooltip.filter',
  });

  const actionButtons = useMemo(
    () => [
      {
        label: filterToggleLabel,
        iconName: 'filter_alt' as const,
        type: 'secondary' as const,
        action: toggle,
        className: isOpen ? 'gx-border-primary' : '',
      },
      ...headerActionButtons,
    ],
    [filterToggleLabel, headerActionButtons, isOpen, toggle],
  );

  return (
    <>
      <SecondaryPanel.Panel
        title={intl.formatMessage({ id: 'dashboards.chart.filters' })}
        initialValues={{}}
      >
        {filterPanel}
      </SecondaryPanel.Panel>

      <BoxContainer>
        <BoxContainer fullContent fixed bottomBorder>
          <FilterContainer
            title={<Title.Header value={<IntlMessages id="workJourney.management.title" />} />}
            actionButtons={actionButtons}
          />
        </BoxContainer>

        <FilterActiveTags.Container>
          <FilterActiveTags.Bar>
            <FilterActiveTags.ScrollArea>
              <FilterActiveTags
                onAddFilter={toggle}
                customValues={filterTagValues}
                onCustomFilterChange={handleCustomFilterChange}
              />
            </FilterActiveTags.ScrollArea>
          </FilterActiveTags.Bar>
        </FilterActiveTags.Container>

        <BoxContainer fullContent loading={loading} error={error}>
          <SecondaryPanel.Content className={styles.secondaryPanelContent}>
            <div className={styles.mainContent} data-testid="work-journey-management-main">
              <WorkJourneyManagementTabs
                activeTab={activeTab}
                tabItems={tabItems}
                onTabChange={handleTabChange}
              />

              {activeTab === 'requires_attention' &&
                (attentionItems.length === 0 && !loading ? (
                  <WorkJourneyManagementEmptyState
                    messageId="workJourney.management.empty.attention"
                    filtered={filtersActive}
                  />
                ) : (
                  <WorkJourneyAttentionSection
                    items={attentionItems}
                    loading={loading}
                    total={attentionTotal}
                    page={attentionPage}
                    pageSize={attentionPageSize}
                    onPageChange={onAttentionPageChange}
                  />
                ))}

              {activeTab === 'journeys' && (
                <WorkJourneyJourneysSection
                  summary={journeysSummary}
                  items={journeyItems}
                  loading={loading}
                  total={journeysTotal}
                  page={journeysPage}
                  pageSize={journeysPageSize}
                  onPageChange={onJourneysPageChange}
                  onPreviousMonth={goToPreviousMonth}
                  onNextMonth={goToNextMonth}
                />
              )}

              {activeTab === 'calendar' && (
                <WorkJourneyCalendarSection
                  summary={calendarSummary}
                  cells={calendarCells}
                  loading={loading}
                  onPreviousMonth={goToPreviousCalendarMonth}
                  onNextMonth={goToNextCalendarMonth}
                />
              )}
            </div>
          </SecondaryPanel.Content>
        </BoxContainer>
      </BoxContainer>

      <AbsenceFormDrawer
        visible={scheduleAbsence.open}
        absence={null}
        onClose={scheduleAbsence.closeDrawer}
        onSave={scheduleAbsence.handleSave}
      />

      <PlanMonthlyJourneyDrawer
        open={planDrawerOpen}
        onClose={closePlanDrawer}
        onSaved={refreshActiveTab}
      />

      <WorkJourneyManagementRoutes url={url} />
    </>
  );
};

export const WorkJourneyManagement = () => (
  <WorkJourneyManagementProvider>
    <WorkJourneyManagementView />
  </WorkJourneyManagementProvider>
);
